/**
 * Defines versioned contracts for private files, reports and controlled Excel imports.
 */
import { z } from "zod";
import { EvidenceMediaTypeSchema } from "./EvidenceContract.js";
const Sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const FileNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(255)
    .refine((value) => !value.includes("/") && !value.includes("\\"));
export const EvidenceClassificationSchema = z.enum([
    "CONSUMER",
    "INTERNAL",
    "INVESTIGATION",
    "RESPONSE_DRAFT",
    "FINAL_RESPONSE",
    "DELIVERY_RECEIPT",
    "OTHER",
]);
export const EvidenceVisibilitySchema = z.enum(["INTERNAL", "CONSUMER"]);
export const FileObjectStatusSchema = z.enum([
    "PENDING_UPLOAD",
    "UPLOADED",
    "VERIFIED",
    "REJECTED",
    "EXPIRED",
]);
export const EvidenceUploadRequestSchema = z
    .object({
    classification: EvidenceClassificationSchema,
    fileName: FileNameSchema,
    mediaType: EvidenceMediaTypeSchema,
    requestId: z.uuid(),
    sizeBytes: z.number().int().positive(),
    visibility: EvidenceVisibilitySchema,
})
    .strict();
export const UploadAuthorizationSchema = z
    .object({
    expiresAt: z.iso.datetime({ offset: true }),
    fileId: z.uuid(),
    headers: z.record(z.string(), z.string()),
    method: z.literal("PUT"),
    resourceId: z.uuid().nullable(),
    url: z.url(),
})
    .strict();
export const CompleteUploadRequestSchema = z
    .object({
    sha256: Sha256Schema,
})
    .strict();
export const FileStatusResponseSchema = z
    .object({
    detectedMediaType: z.string().max(160).nullable(),
    fileId: z.uuid(),
    rejectionCode: z.string().max(120).nullable(),
    status: FileObjectStatusSchema,
})
    .strict();
export const TemporaryDownloadSchema = z
    .object({
    expiresAt: z.iso.datetime({ offset: true }),
    fileName: z.string().min(1).max(255),
    mediaType: z.string().min(1).max(160),
    url: z.url(),
})
    .strict();
export const ReportTypeSchema = z.enum(["CASE_PDF", "CASE_LIST_XLSX"]);
export const ReportStatusSchema = z.enum([
    "PENDING",
    "PROCESSING",
    "COMPLETED",
    "FAILED",
    "EXPIRED",
]);
export const CreateReportRequestSchema = z
    .object({
    publicId: z.string().regex(/^\d+$/).optional(),
    requestId: z.uuid(),
    type: ReportTypeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.type === "CASE_PDF" && value.publicId === undefined) {
        context.addIssue({
            code: "custom",
            message: "CASE_PDF requires publicId.",
            path: ["publicId"],
        });
    }
    if (value.type === "CASE_LIST_XLSX" && value.publicId !== undefined) {
        context.addIssue({
            code: "custom",
            message: "CASE_LIST_XLSX does not accept publicId.",
            path: ["publicId"],
        });
    }
});
export const ReportResponseSchema = z
    .object({
    errorCode: z.string().max(120).nullable(),
    expiresAt: z.iso.datetime({ offset: true }).nullable(),
    fileId: z.uuid().nullable(),
    id: z.uuid(),
    status: ReportStatusSchema,
    type: ReportTypeSchema,
})
    .strict();
export const ReportRequestedEventName = "reports.requested.v1";
export const ReportRequestedEventPayloadSchema = z
    .object({
    companyId: z.uuid(),
    reportId: z.uuid(),
})
    .strict();
export const ImportTypeSchema = z.literal("CASE_PRIORITY_UPDATE");
export const ImportStatusSchema = z.enum([
    "PENDING_UPLOAD",
    "PROCESSING",
    "PREVIEW_READY",
    "APPLYING",
    "APPLIED",
    "FAILED",
]);
export const CreateImportRequestSchema = z
    .object({
    batchKey: z
        .string()
        .trim()
        .min(8)
        .max(120)
        .regex(/^[A-Za-z0-9._:-]+$/),
    fileName: FileNameSchema.refine((value) => value.toLowerCase().endsWith(".xlsx")),
    requestId: z.uuid(),
    sizeBytes: z.number().int().positive(),
    type: ImportTypeSchema,
})
    .strict();
export const ImportRowPreviewSchema = z
    .object({
    errors: z.array(z.string().min(1).max(240)).max(10),
    priority: z.enum(["LOW", "NORMAL", "HIGH", "URGENT"]).nullable(),
    publicId: z.string().regex(/^\d+$/).nullable(),
    reason: z.string().min(1).max(500).nullable(),
    rowNumber: z.number().int().min(2),
})
    .strict();
export const ImportResponseSchema = z
    .object({
    errorCode: z.string().max(120).nullable(),
    errorFileId: z.uuid().nullable(),
    failedRows: z.number().int().nonnegative(),
    id: z.uuid(),
    processedRows: z.number().int().nonnegative(),
    sourceFileId: z.uuid(),
    status: ImportStatusSchema,
    totalRows: z.number().int().nonnegative(),
    type: ImportTypeSchema,
    validRows: z.number().int().nonnegative(),
})
    .strict();
export const ImportPreviewResponseSchema = z
    .object({
    import: ImportResponseSchema,
    rows: z.array(ImportRowPreviewSchema).max(100),
})
    .strict();
export const ImportRequestedEventName = "imports.requested.v1";
export const ImportRequestedEventPayloadSchema = z
    .object({
    companyId: z.uuid(),
    importId: z.uuid(),
})
    .strict();
export const EvidenceUploadedEventName = "evidences.uploaded.v1";
export const EvidenceUploadedEventPayloadSchema = z
    .object({
    companyId: z.uuid(),
    fileId: z.uuid(),
})
    .strict();
//# sourceMappingURL=ArtifactContract.js.map